// @spec GHG-STR-012
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FileText, History } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { GhgDocumentUpload } from '@/features/ghg/components/ocr/GhgDocumentUpload';
import { GhgOcrProgressCard } from '@/features/ghg/components/ocr/GhgOcrProgressCard';
import { GhgOcrReviewDialog } from '@/features/ghg/components/ocr/GhgOcrReviewDialog';
import { useGHGPagePeriod } from '@/features/ghg/hooks/use-ghg-page-period';
import type { GhgDocumentType, GhgExtractedData } from '@/features/ghg/types/ocr';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ghg/activity-data/document-ocr')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.documentOcr'),
  }),
  component: DocumentOcrPage,
});

interface PendingUpload {
  uploadId: string;
  fileName: string;
  documentType: GhgDocumentType;
}

/**
 * Document OCR page for uploading utility bills and fuel receipts
 * for automatic data extraction via Reducto OCR.
 *
 * Provides:
 * - Drag-and-drop upload for supported document types
 * - Real-time OCR processing status
 * - Review and confirmation of extracted data
 * - Upload history
 */
function DocumentOcrPage() {
  const navigate = useNavigate();
  const { periodId } = useGHGPagePeriod();

  // Track pending uploads for progress display
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);

  // Track completed extraction for review
  const [reviewData, setReviewData] = useState<{
    uploadId: string;
    extractedData: GhgExtractedData;
  } | null>(null);

  // Handle successful upload - add to pending list for progress tracking
  const handleUploadComplete = (_uploadId: string) => {
    // The upload response will be stored - we just need the ID to track progress
    toast.success(t('toast.documentUploaded'), {
      description: t('documentOcr.toastOcrStartedDesc'),
    });
  };

  // Handle upload error
  const handleUploadError = (error: Error) => {
    toast.error(t('ghg:toast.uploadFailed'), {
      description: error.message,
    });
  };

  // Handle OCR completion - show extracted data for review
  const handleOcrComplete = (uploadId: string, extractedData: GhgExtractedData) => {
    setReviewData({ uploadId, extractedData });
    // Remove from pending list
    setPendingUploads((prev) => prev.filter((u) => u.uploadId !== uploadId));
  };

  // Handle OCR error
  const handleOcrError = (_uploadId: string, error: string) => {
    toast.error(t('ghg:toast.ocrProcessingFailed'), {
      description: error,
    });
  };

  // Handle OCR retry - keep in pending list
  const handleOcrRetry = (_uploadId: string) => {
    toast.info(t('toast.retryingOCRProcessing'), {
      description: t('documentOcr.toastRetryDesc'),
    });
  };

  // Handle review confirmation
  const handleReviewConfirm = (activityDataId: string) => {
    setReviewData(null);
    toast.success(t('toast.activityDataCreated'), {
      description: t('documentOcr.toastDataSavedDesc'),
    });
    // Navigate to the new entry
    void navigate({
      to: '/ghg/activity-data/$entryId',
      params: { entryId: activityDataId },
    });
  };

  // Handle review cancel
  const handleReviewCancel = () => {
    setReviewData(null);
    toast.info(t('toast.reviewCancelled'), {
      description: t('documentOcr.toastReviewCancelledDesc'),
    });
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-8">
        {/* Header */}
        <header className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('documentOcr.heading')}</h1>
            <p className="mt-1 text-muted-foreground">
              {t('documentOcr.description')}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              void navigate({ to: '/ghg/activity-data' });
            }}
          >
            {t('documentOcr.backToActivityData')}
          </Button>
        </header>

        {/* Tabs */}
        <Tabs defaultValue="upload" className="space-y-6">
            <TabsList>
              <TabsTrigger value="upload" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t('documentOcr.uploadDocument')}
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-2">
                <History className="h-4 w-4" />
                {t('documentOcr.uploadHistory')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upload" className="space-y-6">
              {/* Upload Component */}
              <GhgDocumentUpload
                reportingPeriodId={periodId}
                onUploadComplete={handleUploadComplete}
                onError={handleUploadError}
              />

              {/* Pending Uploads Progress */}
              {pendingUploads.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">{t('documentOcr.processingDocuments')}</h3>
                  {pendingUploads.map((upload) => (
                    <GhgOcrProgressCard
                      key={upload.uploadId}
                      uploadId={upload.uploadId}
                      fileName={upload.fileName}
                      documentType={upload.documentType}
                      onComplete={(extractedData) => {
                        handleOcrComplete(upload.uploadId, extractedData);
                      }}
                      onError={(error) => {
                        handleOcrError(upload.uploadId, error);
                      }}
                      onRetry={() => {
                        handleOcrRetry(upload.uploadId);
                      }}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="history">
              <Card>
                <CardHeader>
                  <CardTitle>{t('documentOcr.uploadHistory')}</CardTitle>
                  <CardDescription>
                    {t('documentOcr.uploadHistoryDesc')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
                    <FileText className="mb-2 h-10 w-10" />
                    <p>{t('documentOcr.uploadHistoryPlaceholder')}</p>
                    <p className="text-sm">{t('documentOcr.uploadToGetStarted')}</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
        </Tabs>
      </div>

      {/* Review Dialog */}
      {reviewData && (
        <GhgOcrReviewDialog
          open={!!reviewData}
          onOpenChange={(open) => {
            if (!open) setReviewData(null);
          }}
          uploadId={reviewData.uploadId}
          extractedData={reviewData.extractedData}
          reportingPeriodId={periodId}
          onConfirm={handleReviewConfirm}
          onCancel={handleReviewCancel}
        />
      )}
    </div>
  );
}
